import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getArticlesByCategory, getCategoryById } from "@/utils/data";

const PAGE_SIZE = 10;

export default function Category() {
  const { categoryId = "" } = useParams();
  const category = getCategoryById(categoryId);

  const [sort, setSort] = useState<"new" | "hot">("new");
  const [activeTag, setActiveTag] = useState("全部");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  usePageMeta(
    category ? `${category.name} - 自慰健康生活资讯` : "分类未找到 - 自慰健康生活资讯",
    category
      ? `${category.name}分类汇集自慰与健康生活相关资讯：${category.description}`
      : "未找到对应的资讯分类。",
    category ? `${category.name},自慰,健康生活,心理健康,性健康` : "自慰,健康生活",
  );

  const baseArticles = useMemo(
    () => (category ? getArticlesByCategory(category.id) : []),
    [category],
  );

  const tags = useMemo(() => {
    const set = new Set<string>();
    baseArticles.forEach((article) => article.tags.forEach((tag) => set.add(tag)));
    return ["全部", ...Array.from(set)];
  }, [baseArticles]);

  const filtered = useMemo(() => {
    let list = [...baseArticles];
    if (activeTag !== "全部") {
      list = list.filter((article) => article.tags.includes(activeTag));
    }
    list.sort((a, b) =>
      sort === "hot" ? b.readCount - a.readCount : a.date < b.date ? 1 : -1,
    );
    return list;
  }, [baseArticles, activeTag, sort]);

  useEffect(() => {
    setPage(1);
  }, [categoryId, sort, activeTag]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageArticles = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  if (!category) {
    return (
      <div className="w-full max-w-[1200px] mx-auto px-4 md:px-8 py-20 text-center">
        <i className="ri-folder-unknow-line text-5xl text-foreground-300"></i>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-900">分类未找到</h1>
        <p className="mt-3 text-sm text-foreground-600">你访问的分类不存在，可能已被移除。</p>
        <Link
          to="/"
          className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors"
        >
          <i className="ri-arrow-left-line"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const sortLabel = sort === "new" ? "最新发布" : "最多阅读";

  return (
    <div className="w-full">
      <div className="w-full bg-background-100 border-b border-background-200">
        <div className="w-full max-w-[1200px] mx-auto px-4 md:px-8 py-8 md:py-10">
          <Breadcrumb items={[{ label: category.name }]} />
          <div className="mt-4 flex items-start gap-4">
            <span className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-2xl shrink-0">
              <i className={category.icon}></i>
            </span>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{category.name}</h1>
              <p className="mt-2 text-sm md:text-base text-foreground-600 leading-relaxed max-w-3xl">
                {category.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full max-w-[1200px] mx-auto px-4 md:px-8 py-8 md:py-10">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 lg:gap-10">
          {/* 侧栏筛选 */}
          <aside className="lg:col-span-1">
            <button
              type="button"
              onClick={() => setFilterOpen((prev) => !prev)}
              className="lg:hidden w-full flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 border border-background-200 text-sm text-foreground-800"
            >
              <span className="flex items-center gap-2">
                <i className="ri-filter-3-line"></i>
                筛选与排序 · {sortLabel}
              </span>
              <i className={`ri-arrow-down-s-line transition-transform ${filterOpen ? "rotate-180" : ""}`}></i>
            </button>

            <div className={`${filterOpen ? "block" : "hidden"} lg:block mt-3 lg:mt-0 lg:sticky lg:top-24`}>
              <div className="rounded-lg bg-background-100 border border-background-200 p-5">
                <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground-900 mb-3">
                  <i className="ri-sort-desc text-primary-600"></i>
                  排序方式
                </h2>
                <div className="flex flex-col gap-2">
                  {(["new", "hot"] as const).map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setSort(option)}
                      className={`text-left px-3 py-2 rounded-md text-sm transition-colors cursor-pointer ${
                        sort === option
                          ? "bg-primary-500 text-background-50"
                          : "text-foreground-700 hover:bg-background-200"
                      }`}
                    >
                      {option === "new" ? "最新发布" : "最多阅读"}
                    </button>
                  ))}
                </div>

                <h2 className="flex items-center gap-2 text-sm font-semibold text-foreground-900 mt-6 mb-3">
                  <i className="ri-price-tag-3-line text-primary-600"></i>
                  标签筛选
                </h2>
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => setActiveTag(tag)}
                      className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer ${
                        activeTag === tag
                          ? "bg-secondary-500 text-background-50"
                          : "bg-background-200 text-foreground-700 hover:bg-secondary-100"
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* 列表 */}
          <div className="lg:col-span-3">
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm text-foreground-600">
                共 <span className="text-foreground-900 font-medium">{filtered.length}</span> 篇资讯
              </p>
              <div className="hidden lg:flex items-center gap-2 text-sm text-foreground-500">
                <i className="ri-filter-3-line"></i>
                当前：{sortLabel}
              </div>
            </div>

            {pageArticles.length === 0 ? (
              <div className="py-20 text-center rounded-lg bg-background-100 border border-background-200">
                <i className="ri-inbox-line text-5xl text-foreground-300"></i>
                <p className="mt-5 text-foreground-700">暂无相关资讯</p>
                <p className="mt-2 text-sm text-foreground-500">试试切换其他标签或排序方式。</p>
                <button
                  type="button"
                  onClick={() => {
                    setActiveTag("全部");
                    setSort("new");
                  }}
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-go-back-line"></i>
                  返回上一层
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                {pageArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            )}

            {totalPages > 1 && (
              <div className="mt-10 flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-700 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                {Array.from({ length: totalPages }, (_, index) => index + 1).map((num) => (
                  <button
                    key={num}
                    type="button"
                    onClick={() => setPage(num)}
                    className={`w-9 h-9 flex items-center justify-center rounded-md text-sm transition-colors cursor-pointer ${
                      num === currentPage
                        ? "bg-primary-500 text-background-50"
                        : "border border-background-200 text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    {num}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-700 hover:bg-background-100 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}